<template>
  <a-modal
    :open="open"
    @cancel="handleClose"
    centered
    :maskClosable="false"
    :title="`Descargar reporte “${sequenceName}”`"
    :width="600"
    :bodyStyle="{ padding: '20px 0' }"
  >
    <span>
      En este reporte, verás la actividad de tu secuencia con tus contactos. Recuerda que solo
      puedes obtener data de 1 mes como máximo.
    </span>
    <a-form layout="vertical" class="from-picker" ref="formRef" :rules="rules" :model="formState">
      <a-form-item class="form-item-picker" name="startDate" label="Fecha de inicio" required>
        <a-date-picker
          v-model:value="formState.startDate"
          valueFormat="YYYY-MM-DD"
          format="DD/MM/YYYY"
        />
      </a-form-item>

      <a-form-item class="form-item-picker" name="endDate" label="Fecha de fin" required>
        <a-date-picker
          v-model:value="formState.endDate"
          valueFormat="YYYY-MM-DD"
          format="DD/MM/YYYY"
        />
      </a-form-item>
    </a-form>

    <template #footer>
      <a-button @click="handleClose">Cancelar</a-button>
      <a-button type="primary" @click="handleDownload" :loading="loading"
        >Descargar reporte</a-button
      >
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { sequenceService } from "@/app/flows/sequences/services/Sequence.service";
import { downloadFile } from "@/app/shared/utils/file";
import { message } from "ant-design-vue";
import { stringToDate, dateToString } from "@/app/shared/utils/dates";
import dayjs from "dayjs";

const props = defineProps<{ sequenceId: number; sequenceName: string; open: boolean }>();

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const today = dayjs();
const oneMonthAgo = today.subtract(1, "month");

const formState = ref({
  startDate: dateToString(oneMonthAgo.toDate()),
  // endDate: dateToString(today.toDate()),
  endDate: null,
});

const formRef = ref();

const handleClose = () => emit("update:open", false);

const rules = {
  startDate: [{ required: true, message: "Campo requerido" }],
  endDate: [
    { required: true, message: "Campo requerido" },
    {
      validator: (value: string) => {
        const startDate = stringToDate(formState.value.startDate);
        const valueDate = stringToDate(formState.value.endDate);
        if (!value) {
          return Promise.resolve();
        }
        if (startDate && valueDate && valueDate < startDate) {
          return Promise.reject("La fecha de fin debe ser mayor que la fecha de inicio");
        } else if (startDate && dayjs(startDate).add(1, "month").isBefore(dayjs(valueDate))) {
          return Promise.reject("Las fechas no deben tener más de un mes de diferencia");
        } else {
          return Promise.resolve();
        }
      },
    },
  ],
};
const loading = ref(false);

const handleDownload = async () => {
  await formRef.value.validate();

  loading.value = true;

  try {
    const response = await sequenceService.downloadReport(props.sequenceId, formState.value);

    downloadFile(response, `sequence-${props.sequenceId}.xlsx`, { useResponseFileName: true });
  } catch (error) {
    console.error(error);
    message.error("Ha ocurrido un error");
  }

  loading.value = false;
};
</script>

<style scoped lang="sass">
.modal
  &__body
    color: $gray-9
    height: 400px
    display: grid
    grid-template-columns: repeat(4, 1fr)
    grid-template-rows: repeat(3, 1fr)
    gap: 12px

  &__picker
    margin-bottom: 12px

  &__paragraph
    margin: 16px 0

.radial
  display: grid
  place-content: center

  .progress
    text-align: center

.card
  border: 1px solid $gray-5
  border-radius: 6px
  padding: 5px 10px

  &__title
    font-weight: 600
    margin-bottom: 12px

  &__icon
    font-size: 32px
    margin: 4px 0

    & img
      display: block
      margin: 6px 0
      height: 36px

    &--red
      color: $red-6

  &__value
    font-size: 30px
    line-height: 38px
    margin-bottom: -4px

  &__accordion
    padding: 0

  &--stat
    display: flex
    flex-direction: column
    align-items: center
    padding: 20px 0

  &:nth-child(1)
    grid-column: 1 / 3
    grid-row: 1 / 3

  &:nth-child(2)
    grid-column: 1 / 1
    grid-row: 3 / 4

  &:nth-child(3)
    grid-column: 2 / 2
    grid-row: 3 / 4

  &:nth-child(4)
    grid-column: 3 / 5
    grid-row: 1 / 4

.from-picker
  display: flex
  margin-top: 20px
  .ant-form-item
    width: 50%
    margin-right: 10px
  .ant-picker
    width: 100%
</style>
